import { ReactNode } from 'react';
import { MicFailure } from './audio/learn_mic_capture.ts';
import { ButtonColumn, PrimaryButton, SecondaryButton } from './learn_buttons.tsx';
import { PromptSubtext, ScreenBody, ScreenPrompt, Slack } from './learn_screen_parts.tsx';

// The failure screen every listening Learn exercise shows (issues #3473, #3481, #3510, #3518):
// one large prompt, one line saying what went wrong, and a way out - never a dead screen. A denied
// microphone's line points the singer to their settings (issue #3822), since once a singer has said
// no, neither the apps nor the browsers ask again; it names no steps, which differ by platform, and
// there is no button, since the page cannot open the settings itself. The prompt names what this
// exercise wanted the microphone for, so each exercise passes its own; the line under it is the
// same wherever the failure can happen, so it lives here. Presentational.
//
// An exercise that can carry on after the failure passes `onRetry` and gets Try again above its way
// back; one whose failure ends the run (the Range Finder's microphone) passes none and gets the way
// back alone. The way back is the exercise's to name: a module's class screen ("Back to Pitch",
// issue #3719), or wherever the singer came from for the Range Finder ("Back to Learn", "Back to
// Pitch", "Back"; learn_navigation.tsx). What Try again means is the
// exercise's business; this screen only reports.

// Anything that can stop an exercise listening: the microphone would not open, or we heard the
// room for long enough without ever hearing a singer in it.
export type LearnFailure = MicFailure | 'tooNoisy';

// The one line under the prompt (issues #3481, #3510, #3822)
export const failureSubtext: Record<LearnFailure, string> = {
  denied: 'Turn on the microphone for Sing Your Part in your settings.',
  unavailable: "We couldn't find a microphone on this device.",
  busy: 'Another app or tab is using the microphone.',
  unknown: 'Another app or tab is using the microphone.',
  tooNoisy: 'Try a quieter spot, then try again.',
};

// The prompt the screen opens with for an exercise that listens to the singer sing (Pitch Match,
// Interval Training), in the single-prompt style the Range Finder's screens took (issue #3510):
// what the exercise wanted the microphone for, or, when the room was too loud for a note to be made
// of anything, what we heard instead. The line under it is failureSubtext. The mic prompt's cap
// breaks it after "microphone", so neither line ends on a dangling "to"; the noisy one is short
// enough to need no cap. Lifted out of Pitch Match's page so both exercises say the same thing.
export function singingFailurePrompt(failure: LearnFailure): { text: string; widthInChars?: number } {
  if (failure === 'tooNoisy') {
    return { text: "It's a bit noisy here" };
  }
  return { text: 'We need the microphone to hear you sing', widthInChars: 19 };
}

// The ids this screen renders for one exercise's prefix ('range', 'pitch'), so the exercise that
// mounts it and the stories that drive it name the same strings (issue #3518).
export function failureScreenIds(prefix: string) {
  return {
    screen: `${prefix}-failure`,
    retry: `${prefix}-retry-button`,
    back: `${prefix}-failure-back-button`,
  };
}

// `idPrefix` is the exercise's own ('range', 'pitch'), so its screen and buttons keep ids of their
// own; `prompt` is the exercise's own line, capped at `promptWidthInChars` characters where it
// should break somewhere other than the body's own width.
export const LearnFailureScreen = ({
  failure,
  idPrefix,
  prompt,
  promptWidthInChars,
  onRetry,
  backLabel,
  onBack,
}: {
  failure: LearnFailure;
  idPrefix: string;
  prompt: ReactNode;
  promptWidthInChars?: number;
  onRetry?: () => void;
  backLabel: string; // "Back to Pitch", "Back to Learn", "Back"
  onBack: () => void;
}) => {
  const ids = failureScreenIds(idPrefix);
  return (
    <ScreenBody id={ids.screen} data-failure={failure}>
      <ScreenPrompt $widthInChars={promptWidthInChars}>{prompt}</ScreenPrompt>
      <PromptSubtext>{failureSubtext[failure]}</PromptSubtext>
      <Slack />
      {onRetry === undefined ? (
        <PrimaryButton id={ids.back} data-testid={ids.back} onClick={onBack}>
          {backLabel}
        </PrimaryButton>
      ) : (
        <ButtonColumn>
          <PrimaryButton id={ids.retry} data-testid={ids.retry} onClick={onRetry}>
            Try again
          </PrimaryButton>
          <SecondaryButton id={ids.back} data-testid={ids.back} onClick={onBack}>
            {backLabel}
          </SecondaryButton>
        </ButtonColumn>
      )}
    </ScreenBody>
  );
};

// The failure screen of an exercise that listens to the singer sing (Pitch Match, Interval
// Training): the shared screen with singingFailurePrompt and Try again above the way back. What
// Try again resumes - and whether it reopens the microphone first - is the exercise's.
export const SingingFailureScreen = ({
  failure,
  idPrefix,
  onRetry,
  backLabel,
  onBack,
}: {
  failure: LearnFailure;
  idPrefix: string;
  onRetry: () => void;
  backLabel: string;
  onBack: () => void;
}) => {
  const { text, widthInChars } = singingFailurePrompt(failure);
  return (
    <LearnFailureScreen
      failure={failure}
      idPrefix={idPrefix}
      prompt={text}
      promptWidthInChars={widthInChars}
      onRetry={onRetry}
      backLabel={backLabel}
      onBack={onBack}
    />
  );
};
